// pages/strategic-playbook/decisions/new.js
// ════════════════════════════════════════════════════
// DECISION LOG — capture form
// ════════════════════════════════════════════════════

import { useState } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Link from 'next/link';
import {
  CONVICTION_TIERS,
  TIME_HORIZONS,
  createDecision,
} from '../../../lib/strategic-playbook/decisions';

const PILLARS = [
  { id: 1, label: 'Vision' },
  { id: 2, label: 'Talent' },
  { id: 3, label: 'Development' },
  { id: 4, label: 'Strategy' },
  { id: 5, label: 'Decision Architecture' },
  { id: 6, label: 'Resilience' },
  { id: 7, label: 'Culture' },
  { id: 8, label: 'Legacy' },
];

export default function NewDecision() {
  const router = useRouter();
  const [decision, setDecision] = useState('');
  const [thesis, setThesis] = useState('');
  const [fears, setFears] = useState('');
  const [successCriteria, setSuccessCriteria] = useState('');
  const [failureCriteria, setFailureCriteria] = useState('');
  const [convictionTier, setConvictionTier] = useState('position');
  const [pillars, setPillars] = useState([]);
  const [timeHorizon, setTimeHorizon] = useState('medium');
  const [error, setError] = useState(null);

  const togglePillar = (id) => {
    setPillars((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  function handleSubmit(e) {
    e.preventDefault();
    if (decision.trim().length < 10) {
      setError('Describe the decision in more detail (at least 10 characters).');
      return;
    }
    const created = createDecision({
      decision: decision.trim(),
      thesis: thesis.trim(),
      fears: fears.trim(),
      successCriteria: successCriteria.trim(),
      failureCriteria: failureCriteria.trim(),
      convictionTier,
      pillars,
      timeHorizon,
    });
    if (created) {
      router.push(`/strategic-playbook/decisions/${created.id}`);
    }
  }

  return (
    <div className="sp-root">
      <Head>
        <title>Log Decision · Strategic Intelligence</title>
      </Head>

      <div className="sp-page">
        <DecisionsTopbar />

        <div style={{ marginBottom: 24 }}>
          <Link href="/strategic-playbook/decisions" className="sp-reader-back">
            ← Decision Log
          </Link>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.20em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
            Capture
          </div>
          <h1 style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 32, lineHeight: 1.2, color: 'var(--sp-warm-cream)', margin: '0 0 14px 0', letterSpacing: '-0.01em' }}>
            Log a Decision
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.65, color: 'rgba(232, 221, 196, 0.78)', maxWidth: 720, margin: 0 }}>
            Capture <em>at the moment</em>, not after. Conviction tier locks the size discipline.
            Thesis + fears + exit criteria force pre-commitment that protects you from
            post-hoc rationalization.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <FormBlock title="The Decision" required>
            <textarea
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
              rows={4}
              placeholder="Describe what you're deciding. Be specific. 'Sized up NVDA from 4% to 9% allocation' beats 'bought more tech.'"
              style={{ ...inputStyle, resize: 'vertical' }}
              autoFocus
            />
          </FormBlock>

          <FormBlock title="Conviction Tier" required>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {CONVICTION_TIERS.map((tier) => (
                <button
                  key={tier.id}
                  type="button"
                  onClick={() => setConvictionTier(tier.id)}
                  style={{
                    textAlign: 'left',
                    padding: '12px 14px',
                    background: convictionTier === tier.id ? 'var(--sp-brass-bg)' : 'transparent',
                    border: `1px solid ${convictionTier === tier.id ? 'var(--sp-aged-brass)' : 'var(--sp-divider)'}`,
                    borderRadius: 'var(--sp-radius)',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontFamily: 'var(--sp-font-serif)', fontSize: 14, color: convictionTier === tier.id ? 'var(--sp-warm-cream)' : 'rgba(232, 221, 196, 0.85)' }}>
                      {tier.label}
                    </span>
                    <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 11, color: tier.color }}>
                      {tier.range}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, lineHeight: 1.5, color: 'rgba(232, 221, 196, 0.60)' }}>{tier.description}</div>
                </button>
              ))}
            </div>
          </FormBlock>

          <FormBlock title="Thesis (why I'm doing this)">
            <textarea
              value={thesis}
              onChange={(e) => setThesis(e.target.value)}
              rows={3}
              placeholder="What's the case? Why now?"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </FormBlock>

          <FormBlock title="Fears (what could go wrong)">
            <textarea
              value={fears}
              onChange={(e) => setFears(e.target.value)}
              rows={3}
              placeholder="Be honest with yourself. What are you worried about? What could blindside you?"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </FormBlock>

          <FormBlock title="Success criteria (how I'll know it's working)">
            <textarea
              value={successCriteria}
              onChange={(e) => setSuccessCriteria(e.target.value)}
              rows={2}
              placeholder="What specific signs would tell you the thesis is confirming?"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </FormBlock>

          <FormBlock title="Failure criteria (when I'll abort)">
            <textarea
              value={failureCriteria}
              onChange={(e) => setFailureCriteria(e.target.value)}
              rows={2}
              placeholder="Pre-commit to your exit. What would make you cut the position / abandon the plan?"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </FormBlock>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }} className="form-grid">
            <FormBlock title="Pillars" inline>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {PILLARS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePillar(p.id)}
                    className={`sp-tag sp-pillar-bg-${p.id} sp-pillar-${p.id}`}
                    style={{
                      cursor: 'pointer',
                      opacity: pillars.length === 0 || pillars.includes(p.id) ? 1 : 0.4,
                      outline: pillars.includes(p.id) ? '1px solid var(--sp-aged-brass)' : 'none',
                      outlineOffset: 1,
                      border: 'none',
                      fontFamily: 'var(--sp-font-mono)',
                      fontSize: 9,
                      textTransform: 'uppercase',
                      letterSpacing: '0.10em',
                      padding: '4px 10px',
                    }}
                  >
                    P{p.id} {p.label}
                  </button>
                ))}
              </div>
            </FormBlock>

            <FormBlock title="Time Horizon" inline>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {TIME_HORIZONS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTimeHorizon(t.id)}
                    style={{
                      textAlign: 'left',
                      padding: '6px 12px',
                      background: timeHorizon === t.id ? 'var(--sp-brass-bg)' : 'transparent',
                      border: `1px solid ${timeHorizon === t.id ? 'var(--sp-aged-brass)' : 'var(--sp-divider)'}`,
                      borderRadius: 'var(--sp-radius)',
                      cursor: 'pointer',
                      fontSize: 12,
                      color: timeHorizon === t.id ? 'var(--sp-warm-cream)' : 'rgba(232, 221, 196, 0.75)',
                    }}
                  >
                    <span style={{ fontFamily: 'var(--sp-font-serif)' }}>{t.label}</span>
                    <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: 'rgba(232, 221, 196, 0.45)', marginLeft: 6 }}>
                      {t.detail}
                    </span>
                  </button>
                ))}
              </div>
            </FormBlock>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="submit"
              disabled={decision.trim().length < 10}
              style={{
                padding: '14px 28px',
                background: decision.trim().length < 10 ? 'transparent' : 'var(--sp-brass-bg)',
                border: `1px solid ${decision.trim().length < 10 ? 'var(--sp-divider)' : 'var(--sp-aged-brass)'}`,
                color: decision.trim().length < 10 ? 'rgba(232, 221, 196, 0.40)' : 'var(--sp-aged-brass)',
                borderRadius: 'var(--sp-radius-card)',
                fontFamily: 'var(--sp-font-mono)',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.16em',
                cursor: decision.trim().length < 10 ? 'not-allowed' : 'pointer',
              }}
            >
              Save Decision →
            </button>
            <Link
              href="/strategic-playbook/decisions"
              style={{
                fontFamily: 'var(--sp-font-mono)',
                fontSize: 11,
                color: 'rgba(232, 221, 196, 0.55)',
                letterSpacing: '0.10em',
                textTransform: 'uppercase',
                textDecoration: 'none',
              }}
            >
              Cancel
            </Link>
            {error && <div style={{ fontSize: 12, color: 'var(--sp-oxblood)', fontFamily: 'var(--sp-font-mono)' }}>{error}</div>}
          </div>
        </form>
      </div>

      <style jsx>{`
        @media (max-width: 700px) {
          .form-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function FormBlock({ title, required, inline, children }) {
  return (
    <div style={{ marginBottom: inline ? 0 : 22 }}>
      <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'var(--sp-aged-brass)', marginBottom: 10 }}>
        {title}
        {required && <span style={{ color: 'var(--sp-oxblood)', marginLeft: 6 }}>*</span>}
      </div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  background: 'var(--sp-charcoal-black)',
  border: '1px solid var(--sp-divider)',
  borderRadius: 'var(--sp-radius)',
  color: 'var(--sp-warm-cream)',
  fontFamily: 'var(--sp-font-body)',
  fontSize: 14,
  lineHeight: 1.6,
  outline: 'none',
};

function DecisionsTopbar() {
  return (
    <div className="sp-topbar">
      <div>
        <h1>Strategic Intelligence</h1>
        <div className="sp-topbar-meta">Decision Log · new entry</div>
      </div>
      <nav className="sp-topbar-nav">
        <Link href="/strategic-playbook">Dashboard</Link>
        <Link href="/strategic-playbook/library">Library</Link>
        <Link href="/strategic-playbook/tools">Tools</Link>
        <Link href="/strategic-playbook/decisions" className="sp-active">Decisions</Link>
        <Link href="/strategic-playbook/simulate">Simulate</Link>
      </nav>
    </div>
  );
}
